import { useEffect, useRef, useState } from 'react';
import { tecladoDaTabela } from '../../tecladoDaTabela';
import { metro, type MetroSpt } from '../../api/spt';
import { linhasDaTabela } from './perfil';

/**
 * A tabela do boletim, metro a metro — onda 356 (RN-276, S01). Só os golpes
 * recebem o foco (Enter e Tab passam ao próximo golpe, Shift volta — o
 * `tecladoDaTabela`, P-UI-49); a penetração já vem 15 e se muda no clique,
 * destacada quando difere. A linha grava quando o cursor sai dela; N' e N
 * voltam do banco (fn_spt_n). A última linha é o próximo metro, em branco.
 */

type Rascunho = { g: string[]; p: string[] };

const doMetro = (m: MetroSpt | null): Rascunho => ({
  g: m ? [m.golpes_1, m.golpes_2, m.golpes_3].map((x) => (x == null ? '' : String(x))) : ['', '', ''],
  p: m ? [m.penet_1, m.penet_2, m.penet_3].map((x) => (x == null ? '' : String(x))) : ['15', '15', '15'],
});

const num = (s: string) => (s.trim() === '' ? null : Number(s));

export function TabelaDeMetros({ metros, fim, editavel, alturaDaLinha, aoGravar, aoMarcarNr }: {
  metros: MetroSpt[];
  fim: number | null;
  editavel: boolean;
  alturaDaLinha: number;
  aoGravar: (prof: number, golpes: (number | null)[], penet: (number | null)[]) => Promise<void>;
  /** 362 · NBR 6484 5.2.3.16: marca a amostra não recuperada do metro (fora do caminho do teclado). */
  aoMarcarNr?: (m: MetroSpt, semRecuperacao: boolean) => void;
}) {
  const linhas = linhasDaTabela(metros, fim);
  const [rasc, setRasc] = useState<Record<string, Rascunho>>({});
  const [gravando, setGravando] = useState<number | null>(null);

  /* O metro que recebe o cursor quando a gravação voltar (o próximo, que ainda não existe na tela). */
  const focarDepois = useRef<number | null>(null);
  const tabela = useRef<HTMLTableElement>(null);

  useEffect(() => {
    setRasc({});
    const prof = focarDepois.current;
    if (prof == null) return;
    focarDepois.current = null;
    const el = tabela.current?.querySelector<HTMLInputElement>(`input[data-prof="${prof}"][data-seg="0"]`);
    if (el) { el.focus(); el.select(); }
  }, [metros]);

  const valor = (prof: number, m: MetroSpt | null) => rasc[prof] ?? doMetro(m);
  const mudar = (prof: number, m: MetroSpt | null, campo: 'g' | 'p', i: number, v: string) => {
    const atual = valor(prof, m);
    const novo = { g: [...atual.g], p: [...atual.p] };
    novo[campo][i] = v.replace(/\D/g, '').slice(0, 3);
    setRasc((r) => ({ ...r, [prof]: novo }));
  };
  /** Grava a linha se ela mudou; devolve se gravou. */
  const gravar = async (prof: number, m: MetroSpt | null): Promise<boolean> => {
    const r = rasc[prof];
    if (!r) return false;
    const orig = doMetro(m);
    if (JSON.stringify(r) === JSON.stringify(orig)) return false;
    if (!m && r.g.every((x) => x === '')) return false;
    setGravando(prof);
    try { await aoGravar(prof, r.g.map(num), r.p.map(num)); return true; } finally { setGravando(null); }
  };
  const sair = async (prof: number, m: MetroSpt | null, e: React.FocusEvent<HTMLElement>) => {
    const linha = e.currentTarget;
    if (e.relatedTarget instanceof Node && linha.contains(e.relatedTarget)) return;
    if (focarDepois.current === prof + 1) return; // a gravação já foi pedida pelo teclado
    await gravar(prof, m);
  };
  /**
   * Enter ou Tab no 3º golpe da ÚLTIMA linha (o metro novo): a linha seguinte ainda não existe na tela — ela nasce
   * quando a gravação volta. Grava agora e leva o cursor ao 1º golpe do metro seguinte (pedido do Humberto,
   * 30/10/2026). Nas outras linhas, o `tecladoDaTabela` já passa à linha de baixo.
   */
  const noTerceiro = (prof: number, m: MetroSpt | null, ultima: boolean, e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.key !== 'Enter' && e.key !== 'Tab') || e.shiftKey || e.ctrlKey || e.altKey || e.metaKey || !ultima) return;
    e.preventDefault();
    e.stopPropagation();
    const proximo = Math.round((prof + 1) * 100) / 100;
    const cabe = fim == null || proximo + 0.45 <= fim + 0.001;
    focarDepois.current = cabe ? proximo : null;
    void gravar(prof, m).then((gravou) => { if (!gravou) focarDepois.current = null; })
      .catch(() => { focarDepois.current = null; });
  };

  return (
    <table ref={tabela} className="spt-tabela" {...tecladoDaTabela('spt-metros')} style={{ ['--spt-linha' as string]: `${alturaDaLinha}px` }}>
      <thead>
        <tr>
          <th scope="col">Prof. (m)</th>
          <th scope="col" colSpan={2}>1º golpes/cm</th>
          <th scope="col" colSpan={2}>2º golpes/cm</th>
          <th scope="col" colSpan={2}>3º golpes/cm</th>
          <th scope="col" title="30 cm iniciais">N'</th>
          <th scope="col" title="30 cm finais">N</th>
          <th scope="col" title="Amostra não recuperada (NBR 6484, 5.2.3.16)">NR</th>
        </tr>
      </thead>
      <tbody>
        <tr className="spt-linha-vazia" aria-hidden="true"><td colSpan={10}>0,00 – 1,00 · sem ensaio</td></tr>
        {linhas.map(({ prof, m }, k) => {
          const r = valor(prof, m);
          const ultima = k === linhas.length - 1;
          return (
            <tr key={prof} data-linha-teclado="" onBlur={(e) => sair(prof, m, e)}
              className={gravando === prof ? 'spt-gravando' : m ? undefined : 'spt-proximo'}>
              <th scope="row" className="mono">{metro(prof)}</th>
              {[0, 1, 2].map((i) => [
                <td key={`g${i}`}>
                  <input className="spt-cel" inputMode="numeric" disabled={!editavel} value={r.g[i]}
                    data-prof={prof} data-seg={i}
                    aria-label={`Golpes do ${i + 1}º segmento a ${metro(prof)} m`}
                    onKeyDown={i === 2 ? (e) => noTerceiro(prof, m, ultima, e) : undefined}
                    onChange={(e) => mudar(prof, m, 'g', i, e.target.value)} />
                </td>,
                <td key={`p${i}`}>
                  <input className={`spt-cel spt-penet${r.p[i] !== '15' ? ' spt-difere' : ''}`} inputMode="numeric"
                    tabIndex={-1} disabled={!editavel} value={r.p[i]}
                    aria-label={`Penetração em cm do ${i + 1}º segmento a ${metro(prof)} m`}
                    onChange={(e) => mudar(prof, m, 'p', i, e.target.value)} />
                </td>,
              ])}
              <td className="mono spt-nini">{m?.n_ini_texto ?? ''}</td>
              <td className="mono spt-n">{m?.n_texto ?? ''}</td>
              <td className="spt-nr">
                {m && (
                  <input type="checkbox" tabIndex={-1} disabled={!editavel || !aoMarcarNr} checked={!!m.sem_recuperacao}
                    aria-label={`Amostra não recuperada a ${metro(prof)} m`}
                    onChange={(e) => aoMarcarNr?.(m, e.target.checked)} />
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
